import { useState } from "react";
import { Plus, X, ImageIcon } from "lucide-react";
import axios from "axios";
import { API, getAuthHeader } from "../utils";
import SponsorListEditor from "./SponsorListEditor";
import { CUSTOM_TEMPLATES } from "./constants";

// Text input field
const TextField = ({ value, onChange, placeholder = "", type = "text" }) => (
  <input
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded focus:outline-none focus:border-orange-500"
  />
);

// Textarea field
const TextareaField = ({ value, onChange, rows = 4 }) => (
  <textarea
    value={value}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded focus:outline-none focus:border-orange-500"
  />
);

// Select dropdown field
const SelectField = ({ value, onChange, options = [] }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded focus:outline-none focus:border-orange-500"
  >
    {options.map(opt => (
      <option key={opt} value={opt}>{opt}</option>
    ))}
  </select>
);

// Color picker field
const ColorField = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <input
      type="color"
      value={value || "#f97316"}
      onChange={(e) => onChange(e.target.value)}
      className="w-12 h-10 rounded cursor-pointer"
    />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded focus:outline-none focus:border-orange-500"
      placeholder="#f97316"
    />
  </div>
);

// Stats list editor
const StatsListField = ({ value = [], onChange }) => {
  const addStat = () => onChange([...value, { value: "", label: "" }]);
  const removeStat = (idx) => onChange(value.filter((_, i) => i !== idx));
  const updateStat = (idx, key, newValue) => {
    const updated = [...value];
    updated[idx] = { ...updated[idx], [key]: newValue };
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      {value.map((stat, idx) => (
        <div key={idx} className="flex gap-2">
          <input
            type="text"
            value={stat.value || ""}
            onChange={(e) => updateStat(idx, 'value', e.target.value)}
            placeholder="Value (e.g., 500+)"
            className="w-32 bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
          />
          <input
            type="text"
            value={stat.label || ""}
            onChange={(e) => updateStat(idx, 'label', e.target.value)}
            placeholder="Label"
            className="flex-1 bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
          />
          <button
            onClick={() => removeStat(idx)}
            className="p-2 text-red-500 hover:bg-red-500/10 rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        onClick={addStat}
        className="text-sm text-orange-500 hover:text-orange-400 flex items-center gap-1"
      >
        <Plus className="w-3 h-3" /> Add Stat
      </button>
    </div>
  );
};

// Recent videos list editor
const RecentVideoListField = ({ value = [], onChange }) => {
  const addVideo = () => onChange([...value, { title: "", url: "", date: "" }]);
  const removeVideo = (idx) => onChange(value.filter((_, i) => i !== idx));
  const updateVideo = (idx, key, newValue) => {
    const updated = [...value];
    updated[idx] = { ...updated[idx], [key]: newValue };
    onChange(updated);
  };

  const getYouTubeThumbnail = (url) => {
    const match = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
  };

  return (
    <div className="space-y-3">
      {value.map((video, idx) => (
        <div key={idx} className="p-3 bg-zinc-800/50 border border-zinc-700 rounded space-y-2">
          <div className="flex items-start gap-3">
            {video.url && getYouTubeThumbnail(video.url) && (
              <div className="w-24 h-16 bg-zinc-900 flex-shrink-0 overflow-hidden">
                <img 
                  src={getYouTubeThumbnail(video.url)}
                  alt="Thumbnail"
                  className="w-full h-full object-cover"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
            <div className="flex-1 space-y-2">
              <input
                type="text"
                value={video.title || ""}
                onChange={(e) => updateVideo(idx, 'title', e.target.value)}
                placeholder="Video Title"
                className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
              />
              <input
                type="text"
                value={video.url || ""}
                onChange={(e) => updateVideo(idx, 'url', e.target.value)}
                placeholder="YouTube URL"
                className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
              />
              <input
                type="date"
                value={video.date || ""}
                onChange={(e) => updateVideo(idx, 'date', e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded"
              />
            </div>
            <button
              onClick={() => removeVideo(idx)}
              className="p-2 text-red-500 hover:bg-red-500/10 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      <button
        onClick={addVideo}
        className="text-sm text-orange-500 hover:text-orange-400 flex items-center gap-1"
      >
        <Plus className="w-3 h-3" /> Add Video
      </button>
    </div>
  );
};

// Hero image editor with position/scale/blur controls
const HeroImageEditor = ({ section, updateSectionData }) => {
  const [uploading, setUploading] = useState(false);
  
  const imageUrl = section.data?.background_image || "";
  const posX = section.data?.image_position_x ?? 50;
  const posY = section.data?.image_position_y ?? 50;
  const scale = section.data?.image_scale ?? 100;
  const blurAmount = section.data?.image_blur ?? 0;

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/uploads/image`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      updateSectionData(section.id, 'background_image', res.data.url);
    } catch (err) {
      alert('Error uploading image: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const presets = [
    { label: "Center", posX: 50, posY: 50, scale: 100, blur: 0 },
    { label: "Top", posX: 50, posY: 0, scale: 100, blur: 0 },
    { label: "Zoom", posX: 50, posY: 50, scale: 150, blur: 0 },
    { label: "Blur BG", posX: 50, posY: 50, scale: 110, blur: 5 },
  ];

  const applyPreset = (preset) => {
    updateSectionData(section.id, 'image_position_x', preset.posX);
    updateSectionData(section.id, 'image_position_y', preset.posY);
    updateSectionData(section.id, 'image_scale', preset.scale);
    updateSectionData(section.id, 'image_blur', preset.blur);
  };

  return (
    <div className="space-y-3">
      {/* Image Input - URL or Upload */}
      <div className="flex gap-2">
        <input
          type="text"
          value={imageUrl}
          onChange={(e) => updateSectionData(section.id, 'background_image', e.target.value)}
          placeholder="Enter image URL or upload..."
          className="flex-1 bg-zinc-900 border border-zinc-700 text-white px-3 py-2 text-sm rounded focus:outline-none focus:border-orange-500"
        />
        <label className={`px-3 py-2 ${uploading ? 'bg-zinc-600' : 'bg-orange-600 hover:bg-orange-700'} text-white text-sm cursor-pointer rounded flex items-center gap-2 transition-colors`}>
          <ImageIcon className="w-4 h-4" />
          {uploading ? "..." : "Upload"}
          <input 
            type="file" 
            accept="image/*" 
            onChange={handleImageUpload}
            className="hidden" 
            disabled={uploading}
          />
        </label>
      </div>
      
      {/* Preview with Controls */}
      {imageUrl && (
        <div className="space-y-3 p-3 bg-zinc-900/50 border border-zinc-800 rounded">
          {/* Live Preview */}
          <div 
            className="relative w-full h-32 overflow-hidden rounded border border-zinc-700"
            style={{
              backgroundImage: `url(${imageUrl})`,
              backgroundPosition: `${posX}% ${posY}%`,
              backgroundSize: `${scale}%`,
              backgroundRepeat: 'no-repeat',
              filter: `blur(${blurAmount}px)`
            }}
          >
            <div className="absolute inset-0 flex items-center">
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
              <div className="p-4 relative z-10">
                <h3 className="text-white text-lg font-bold">{section.data?.title || "Title"}</h3>
                <p className="text-zinc-300 text-xs mt-0.5">{section.data?.subtitle || "Subtitle"}</p>
              </div>
            </div>
          </div>
          
          {/* Controls Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="flex justify-between text-[10px] text-zinc-500 uppercase mb-1">
                <span>Position X</span>
                <span className="text-orange-400">{posX}%</span>
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={posX}
                onChange={(e) => updateSectionData(section.id, 'image_position_x', parseInt(e.target.value))}
                className="w-full h-1 accent-orange-500"
              />
            </div>
            <div>
              <label className="flex justify-between text-[10px] text-zinc-500 uppercase mb-1">
                <span>Position Y</span>
                <span className="text-orange-400">{posY}%</span>
              </label>
              <input
                type="range"
                min="0"
                max="100"
                value={posY}
                onChange={(e) => updateSectionData(section.id, 'image_position_y', parseInt(e.target.value))}
                className="w-full h-1 accent-orange-500"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="flex justify-between text-[10px] text-zinc-500 uppercase mb-1">
                <span>Scale</span>
                <span className="text-orange-400">{scale}%</span>
              </label>
              <input
                type="range"
                min="50"
                max="200"
                value={scale}
                onChange={(e) => updateSectionData(section.id, 'image_scale', parseInt(e.target.value))}
                className="w-full h-1 accent-orange-500"
              />
            </div>
            <div>
              <label className="flex justify-between text-[10px] text-zinc-500 uppercase mb-1">
                <span>Blur</span>
                <span className="text-orange-400">{blurAmount}px</span>
              </label>
              <input
                type="range"
                min="0"
                max="20"
                value={blurAmount}
                onChange={(e) => updateSectionData(section.id, 'image_blur', parseInt(e.target.value))}
                className="w-full h-1 accent-orange-500"
              />
            </div>
          </div>
          
          {/* Quick Presets */}
          <div className="flex flex-wrap gap-1">
            {presets.map(preset => (
              <button
                key={preset.label}
                onClick={() => applyPreset(preset)}
                className="px-2 py-0.5 text-[10px] bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white rounded transition-colors"
              >
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Main dynamic field renderer component
const DynamicFieldRenderer = ({ 
  section, 
  field, 
  updateSectionData, 
  recordings = [] 
}) => {
  const value = section.data?.[field.key] || "";
  
  const handleChange = (newValue) => {
    updateSectionData(section.id, field.key, newValue);
  };

  switch (field.type) {
    case "text":
      return <TextField value={value} onChange={handleChange} placeholder={field.placeholder} />;
    
    case "textarea":
      return <TextareaField value={value} onChange={handleChange} rows={field.rows} />;
    
    case "select":
      return <SelectField value={value} onChange={handleChange} options={field.options} />;
    
    case "color":
      return <ColorField value={value} onChange={handleChange} />;
    
    case "stats_list":
      return <StatsListField value={value || []} onChange={handleChange} />;
    
    case "recent_video_list":
      return <RecentVideoListField value={value || []} onChange={handleChange} />;
    
    case "sponsor_list":
      return (
        <SponsorListEditor
          sponsors={value || []}
          onUpdate={handleChange}
        />
      );
    
    case "hero_image_editor":
      return <HeroImageEditor section={section} updateSectionData={updateSectionData} />;
    
    default:
      return <TextField value={value} onChange={handleChange} />;
  }
};

export default DynamicFieldRenderer;
export { 
  TextField, 
  TextareaField, 
  SelectField, 
  ColorField, 
  StatsListField, 
  RecentVideoListField,
  HeroImageEditor 
};
